import React, { useContext, useReducer, useRef } from 'react';

import {
  UnitOfMeasurement,
  UnitOfMeasurementCode,
} from 'constants/unitOfMeasurement';
import { FindCompTabs, FindCompsSearchTab } from 'constants/findCompTabs';
import { SearchCriteriaType } from 'components/FindComps/EditSearchCriteria/types';
import { IRadioItem } from 'components/Inputs/RadioInput';
import { CombinedSuggestion } from 'interfaces/IBoundarySuggestionInputs';
import { IProperty } from 'interfaces/IProperty';
import { UnitEnum } from 'interfaces/IPropertySearch';
import { IdName } from 'interfaces/IdName';
import { FindCompsSearchCriteriaService } from 'services/FindCompsSearchCriteriaService';

import {
  calculateInitialBoundaries,
  calculateInitialProperty,
  calculateInitialTravelDistanceValues,
} from '../components/FindComps/DistanceTravelSearch/utils';

export const DEFAULT_INITIAL_BOUNDARY = {
  inputSearch: '',
  countryCode: '',
  selectedBoundaries: [],
};

interface IFindCompsContext {
  state: FindCompsState;
  dispatch: React.Dispatch<any>;
  setSelectedBoundaries: (boundaries: CombinedSuggestion[]) => void;
  setMapboxProperty: (property: IProperty | null) => void;
  setAvantProperty: (property: IProperty | null) => void;
  clearSmartSearch: () => void;
  resetSmartSearchCallbackRef?: React.MutableRefObject<(() => void) | null>;
  setBoundaries: (boundaries: CombinedSuggestion[]) => void;
  setUnit: (unit: UnitEnum) => void;
  setSelectedTravelDistance: (travelDistance: IRadioItem) => void;
  setTravelMode: (travelMode: IdName) => void;
  setDistanceCount: (distanceCount: number) => void;
  setMinutesCount: (minutesCount: number) => void;
}

type SmartSearchState = {
  selectedBoundaries?: CombinedSuggestion[];
  initialBoundaries?: {
    inputSearch: string;
    countryCode: string;
    selectedBoundaries: CombinedSuggestion[];
  };
  mapboxProperty?: IProperty | null;
  avantProperty?: IProperty | null;
  unit?: string;
  selectedTravelDistance?: IRadioItem;
  travelMode?: IdName;
  distanceCount?: number;
  minutesCount?: number;
};

type FindCompsState = {
  children?: any;
  criteria?: SearchCriteriaType;
  currencySymbol?: string;
  initialCriteria?: SearchCriteriaType;
  measurementSystemCode?: UnitOfMeasurementCode;
  measurementKPI?: UnitOfMeasurement;
  itemsCount?: number;
  isEditingSearchCriteria?: boolean;
  currentTab?: FindCompTabs;
  currentSearchTab?: FindCompsSearchTab;
  isCheckedAll?: boolean;
  checkedIds?: number[];
  uncheckedIds?: number[];
  smartSearch?: SmartSearchState;
};

export enum FindCompsActions {
  reset_criteria,
  set_criteria,
  set_editing_criteria,
  set_initial_criteria,
  set_localization,
  set_totals,
  set_measurement_kpi,
  set_current_tab,
  set_current_search_tab,
  set_all_checked,
  set_checked_ids,
  set_unchecked_ids,
  set_smart_search,
}

const INITIAL_CRITERIA = {
  customCriteria: {},
  leasesCriteria: {},
  timsCriteria: {},
  openSelectionCriteria: {},
  salesCriteria: {},
};

const FindCompsContext = React.createContext<IFindCompsContext | undefined>({
  state: {
    isEditingSearchCriteria: false,
    currentSearchTab: FindCompsSearchTab.MARKET_SEARCH,
    checkedIds: [],
    uncheckedIds: [],
    isCheckedAll: true,
    smartSearch: {
      selectedBoundaries: [],
      initialBoundaries: {
        inputSearch: '',
        countryCode: '',
        selectedBoundaries: [],
      },
      mapboxProperty: null,
      avantProperty: null,
      unit: UnitEnum.MILES,
      selectedTravelDistance: { label: '', value: '' },
      travelMode: { id: 0, name: '' },
      distanceCount: 0,
      minutesCount: 0,
    },
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  dispatch: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedBoundaries: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setMapboxProperty: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setAvantProperty: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  clearSmartSearch: () => {},
  resetSmartSearchCallbackRef: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setBoundaries: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setUnit: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedTravelDistance: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setTravelMode: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setDistanceCount: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setMinutesCount: () => {},
});

const FindCompsReducer = (state: FindCompsState, action: any) => {
  switch (action.type) {
    case FindCompsActions.set_criteria: {
      return { ...state, criteria: action.criteria };
    }

    case FindCompsActions.reset_criteria:
      return { ...state, criteria: {} };

    case FindCompsActions.set_initial_criteria:
      return { ...state, initialCriteria: action.criteria };

    case FindCompsActions.set_editing_criteria:
      return {
        ...state,
        isEditingSearchCriteria: action.isEditingSearchCriteria,
      };

    case FindCompsActions.set_localization:
      return {
        ...state,
        measurementSystemCode: action.measurementSystemCode,
        currencySymbol: action.currencySymbol,
      };

    case FindCompsActions.set_totals:
      return {
        ...state,
        itemsCount: action.itemsCount,
      };

    case FindCompsActions.set_measurement_kpi:
      return {
        ...state,
        measurementKPI: action.measurementKPI,
      };

    case FindCompsActions.set_current_tab:
      return {
        ...state,
        currentTab: action.currentTab,
      };

    case FindCompsActions.set_current_search_tab:
      return {
        ...state,
        currentSearchTab: action.currentSearchTab,
      };

    case FindCompsActions.set_all_checked:
      return {
        ...state,
        isCheckedAll: action.isCheckedAll,
      };

    case FindCompsActions.set_checked_ids:
      return {
        ...state,
        checkedIds: action.checkedIds,
      };

    case FindCompsActions.set_unchecked_ids:
      return {
        ...state,
        uncheckedIds: action.uncheckedIds,
      };

    case FindCompsActions.set_smart_search:
      return {
        ...state,
        smartSearch: {
          ...state.smartSearch,
          ...action.smartSearch,
        },
      };

    default:
      return state;
  }
};

export const FindCompsProvider = (props: FindCompsState) => {
  const resetSmartSearchCallbackRef = useRef<(() => void) | null>(null);

  const {
    customCriteria: initialCustomCriteria,
  } = FindCompsSearchCriteriaService.parseUrlParamsToFilters(
    window.location.search,
  );

  const initialBoundaries = calculateInitialBoundaries(
    initialCustomCriteria || {},
  );
  const initialProperty = calculateInitialProperty(initialCustomCriteria || {});

  const initialTravelDistanceValues = calculateInitialTravelDistanceValues(
    initialCustomCriteria || {},
  );

  const [state, dispatch] = useReducer(FindCompsReducer, {
    ...props,
    currentSearchTab: FindCompsSearchTab.MARKET_SEARCH,
    isEditingSearchCriteria: false,
    criteria: INITIAL_CRITERIA,
    checkedIds: [],
    uncheckedIds: [],
    isCheckedAll: true,
    smartSearch: {
      selectedBoundaries: initialBoundaries.selectedBoundaries,
      mapboxProperty: null,
      avantProperty: initialProperty,
      unit: initialTravelDistanceValues.unit,
      selectedTravelDistance:
        initialTravelDistanceValues.selectedTravelDistance,
      travelMode: initialTravelDistanceValues.travelMode,
      distanceCount: 0,
      minutesCount: initialTravelDistanceValues.minutesCount,
    },
  });

  const setSelectedBoundaries = (boundaries: CombinedSuggestion[]) => {
    dispatch({
      type: FindCompsActions.set_smart_search,
      smartSearch: {
        selectedBoundaries: boundaries,
      },
    });
  };

  const setMapboxProperty = (property: IProperty | null) => {
    dispatch({
      type: FindCompsActions.set_smart_search,
      smartSearch: {
        mapboxProperty: property,
      },
    });
  };

  const setAvantProperty = (property: IProperty | null) => {
    dispatch({
      type: FindCompsActions.set_smart_search,
      smartSearch: {
        avantProperty: property,
      },
    });
  };

  const setBoundaries = (boundaries: CombinedSuggestion[]) => {
    dispatch({
      type: FindCompsActions.set_smart_search,
      smartSearch: {
        selectedBoundaries: boundaries,
      },
    });
  };

  const setUnit = (unit: UnitEnum) => {
    dispatch({
      type: FindCompsActions.set_smart_search,
      smartSearch: {
        unit,
      },
    });
  };

  const setSelectedTravelDistance = (travelDistance: IRadioItem) => {
    dispatch({
      type: FindCompsActions.set_smart_search,
      smartSearch: {
        selectedTravelDistance: travelDistance,
      },
    });
  };

  const setTravelMode = (travelMode: IdName) => {
    dispatch({
      type: FindCompsActions.set_smart_search,
      smartSearch: {
        travelMode,
      },
    });
  };

  const setDistanceCount = (distanceCount: number) => {
    dispatch({
      type: FindCompsActions.set_smart_search,
      smartSearch: {
        distanceCount,
      },
    });
  };

  const setMinutesCount = (minutesCount: number) => {
    dispatch({
      type: FindCompsActions.set_smart_search,
      smartSearch: {
        minutesCount,
      },
    });
  };

  const clearSmartSearch = () => {
    dispatch({
      type: FindCompsActions.set_smart_search,
      smartSearch: {
        selectedBoundaries: [],
        mapboxProperty: null,
        avantProperty: null,
      },
    });
  };

  return (
    <FindCompsContext.Provider
      value={{
        state: {
          ...state,
          smartSearch: {
            ...state.smartSearch,
            initialBoundaries: {
              ...DEFAULT_INITIAL_BOUNDARY,
              ...initialBoundaries,
            },
            unit: initialTravelDistanceValues.unit as UnitEnum,
            selectedTravelDistance:
              initialTravelDistanceValues.selectedTravelDistance,
            travelMode: initialTravelDistanceValues.travelMode,
            distanceCount: initialTravelDistanceValues.distanceCount,
            minutesCount: initialTravelDistanceValues.minutesCount,
          },
        },
        dispatch,
        setSelectedBoundaries,
        setMapboxProperty,
        setAvantProperty,
        clearSmartSearch,
        resetSmartSearchCallbackRef,
        setBoundaries,
        setUnit,
        setSelectedTravelDistance,
        setTravelMode,
        setDistanceCount,
        setMinutesCount,
      }}
    >
      {props.children}
    </FindCompsContext.Provider>
  );
};

export const useFindCompsData = () => {
  const context = useContext(FindCompsContext);

  if (context === undefined) {
    throw new Error('useFindCompsData must be used within a FindCompsProvider');
  }

  return context;
};
